import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { Place } from '@/contexts/PlaceNamesContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/popover';

interface AddPeerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddJurisdiction: (place: Place) => void;
  selectedPeers?: Place[]; // Add prop for currently selected peers
  children: React.ReactNode;
}

export const AddPeerDialog: React.FC<AddPeerDialogProps> = ({
  open,
  onOpenChange,
  onAddJurisdiction,
  selectedPeers = [], // Default to empty array
  children,
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Index of the currently highlighted option for keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  // Keep highlighted option visible when navigating with arrow keys
  useEffect(() => {
    if (highlightedIndex === -1) return;
    const optionEl = dropdownRef.current?.querySelector<HTMLButtonElement>(`[data-index='${highlightedIndex}']`);
    optionEl?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const {
    searchInputValue,
    setSearchInputValue,
    placeOptions = [],
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  } = useJurisdictionSearch(showAutocomplete);

  // Filter out already selected peers from the search results
  const filteredPlaceOptions = useMemo(() => {
    if (!selectedPeers || selectedPeers.length === 0) {
      return placeOptions;
    }

    // Create a set of selected peer place_fips for efficient lookup
    // Convert all to strings to handle potential type mismatches
    const selectedPeerFips = new Set(selectedPeers.map(peer => String(peer.place_fips)));
    
    // Filter out any places that are already selected as peers
    // Convert place_fips to string for comparison to handle type mismatches
    const filtered = placeOptions.filter(place => {
      const placeFipsString = String(place.place_fips);
      const isAlreadySelected = selectedPeerFips.has(placeFipsString);
      
      // Also check by name as a fallback in case place_fips doesn't match
      const isAlreadySelectedByName = selectedPeers.some(peer => 
        peer.name.toLowerCase() === place.name.toLowerCase()
      );
      
      return !isAlreadySelected && !isAlreadySelectedByName;
    });
    
    return filtered;
  }, [placeOptions, selectedPeers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setShowAutocomplete(true);

    // Reset highlighted option when the user types
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setShowAutocomplete(true);

    if (filteredPlaceOptions.length > 0) {
      setHighlightedIndex(0);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowAutocomplete(false);
      clearResults();
    }, 150);
  };

  const handleSelectItem = (place: Place & { displayName: string }) => {
    // Use the original place data without the displayName
    const { displayName, ...originalPlace } = place;
    onAddJurisdiction(originalPlace);
    setSearchInputValue('');
    setShowAutocomplete(false);
    clearResults();
    onOpenChange(false);
  };

  // Handle keyboard events (arrow navigation + select)
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || !filteredPlaceOptions || filteredPlaceOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev + 1;
          return next >= filteredPlaceOptions.length ? 0 : next;
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? filteredPlaceOptions.length - 1 : next;
        });
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPlaceOptions.length) {
          handleSelectItem(filteredPlaceOptions[highlightedIndex]);
        }
        break;
      }
      case 'Escape': {
        setShowAutocomplete(false);
        break;
      }
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchInputValue('');
      setShowAutocomplete(false);
      clearResults();
    }
  }, [open]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="center">
        <div>
          <div className="relative">
            <label htmlFor="peer-search" className="sr-only">
              Search for jurisdiction
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                id="peer-search"
                type="text"
                value={searchInputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck="false"
                aria-autocomplete="list"
                className="text-[#CA0079] bg-white w-full px-4 py-3 rounded-[11px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CA0079] focus:border-[#CA0079] text-base pl-12 font-geist"
              />
              
              {/* Persistent magnifying glass icon */}
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center h-full">
                <span className="material-symbols-outlined text-[#CA0079] text-xl opacity-50 leading-none">
                  search
                </span>
              </div>

              {/* Placeholder text */}
              {!searchInputValue && (
                <div className="absolute left-12 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <span className="text-[#CA0079] text-base font-medium uppercase opacity-50 font-geist">
                    Select a jurisdiction
                  </span>
                </div>
              )}
            </div>

            {/* Autocomplete dropdown */}
            {showAutocomplete && filteredPlaceOptions && filteredPlaceOptions.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto"
                role="listbox"
              >
                {filteredPlaceOptions.map((place, index) => (
                  <button
                    key={place.place_fips}
                    data-index={index}
                    onClick={() => handleSelectItem(place)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full text-left px-4 py-3 transition-colors text-base first:rounded-t-[11px] last:rounded-b-[11px] font-geist ${index === highlightedIndex ? 'bg-[#CA0079] text-white' : 'text-[#CA0079] hover:bg-gray-50'}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                  >
                    {place.displayName}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Loading state */}
          {isDebouncing && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 p-4 text-center text-[#CA0079]">
              Searching...
            </div>
          )}
          
          {/* Error state */}
          {searchError && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-red-200 z-50 p-4 text-center text-red-500">
              {searchError}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
